import React, { useEffect, useRef, useState, type JSX } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { useConvexAuth, useMutation } from 'convex/react';
import { api } from 'magical-trails-backend/convex/_generated/api';

import AuthForm from './AuthForm';
import Loading from '../Loading';
import { convexErrorMessage } from '../../lib/convexError';

type Phase = 'redeeming' | 'success' | 'error';

/**
 * /redeem/:token — a player opens an invite link. If signed out, they get a
 * compact auth form; once authenticated the token is redeemed automatically,
 * then we navigate into the trail's first story.
 */
const RedeemInvite: React.FC = (): JSX.Element => {
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const redeem = useMutation(api.trailAccess.redeemInvite);

  const [phase, setPhase] = useState<Phase>('redeeming');
  const [message, setMessage] = useState<string | null>(null);
  const [trailName, setTrailName] = useState<string | null>(null);
  // Whether the redeem response actually gave us a story to route into. When
  // false (a successful redeem with an empty storyCodes list — e.g. an
  // invite to a trail with no published stories yet), the "Taking you
  // there…" copy would otherwise strand the player forever with nothing to
  // do — show an account link instead.
  const [willRedirect, setWillRedirect] = useState(true);
  // Guards the effect so the mutation fires exactly once per authenticated
  // session (React 18 StrictMode double-invokes effects in dev).
  const attempted = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || attempted.current) return;
    attempted.current = true;
    setPhase('redeeming');

    let redirectTimer: ReturnType<typeof setTimeout> | undefined;
    redeem({ token })
      .then((result) => {
        setPhase('success');
        setTrailName(result.trailName);
        const firstStory = result.storyCodes[0];
        if (firstStory) {
          setWillRedirect(true);
          // Brief pause so the success message is seen before we route in.
          redirectTimer = setTimeout(() => history.push(`/${firstStory}`), 900);
        } else {
          setWillRedirect(false);
        }
      })
      .catch((error) => {
        setPhase('error');
        setMessage(
          convexErrorMessage(
            error,
            'This invite link could not be redeemed. It may be invalid or expired.'
          )
        );
      });

    return () => {
      // Never navigate after unmount.
      if (redirectTimer !== undefined) clearTimeout(redirectTimer);
    };
  }, [isAuthenticated, token, redeem, history]);

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return (
      <Screen>
        <AuthForm
          heading="You've been invited!"
          subheading="Sign in or create an account to claim your trail."
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="bg-mt-surface flex w-72 flex-col items-center gap-4 rounded-2xl p-6 text-center shadow-2xl">
        {phase === 'redeeming' && (
          <>
            <h1 className="text-mt-ink-soft text-xl font-bold">
              Claiming your trail…
            </h1>
            <p className="text-mt-ink-muted text-sm">One moment.</p>
          </>
        )}
        {phase === 'success' && (
          <>
            <h1 className="text-mt-ink-soft text-xl font-bold">
              You're in! 🎉
            </h1>
            <p className="text-mt-ink-muted text-sm">
              {trailName ? `“${trailName}” is ready.` : 'Your trail is ready.'}{' '}
              {willRedirect
                ? 'Taking you there…'
                : "It doesn't have a story to jump into yet."}
            </p>
            {!willRedirect && (
              <Link
                to="/account"
                className="text-mt-primary text-sm font-semibold hover:underline"
              >
                Go to my account
              </Link>
            )}
          </>
        )}
        {phase === 'error' && (
          <>
            <h1 className="text-mt-ink-soft text-xl font-bold">
              We couldn't open this invite
            </h1>
            <p className="text-mt-ink-muted text-sm">{message}</p>
            <Link
              to="/account"
              className="text-mt-primary text-sm font-semibold hover:underline"
            >
              Go to my account
            </Link>
          </>
        )}
      </div>
    </Screen>
  );
};

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="start-screen flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat">
      {children}
    </div>
  );
}

export default RedeemInvite;
